import { useState } from 'react';
import { InputDatePicker, TimePicker } from '@crono-one/design-system';
import { parseDateValueFromMultipleFormats } from 'crono-fe-common/utils';
import { FeConstants } from 'constants/FeConstants';
import { getDateYearsFromNow } from 'utils/dateUtils';
import { cn } from 'utils/style-utils';
import { useCloseOnScroll } from 'hooks/useCloseOnScroll';

export interface DateTimeInputProps {
  value: string | undefined;
  disabled: boolean;
  className?: string;
  /** Closes the calendar and the time list when this element scrolls. */
  scrollContainer?: HTMLElement | null;
  onCommit: (value: Date) => void;
}

const DateTimeInput = ({
  value,
  disabled,
  className,
  scrollContainer,
  onCommit,
}: DateTimeInputProps) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  useCloseOnScroll(scrollContainer, isDatePickerOpen, () =>
    setIsDatePickerOpen(false),
  );
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  useCloseOnScroll(scrollContainer, isTimePickerOpen, () =>
    setIsTimePickerOpen(false),
  );

  const currentDate = parseDateValueFromMultipleFormats(value) ?? undefined;

  const timeString = currentDate
    ? `${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}`
    : null;

  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) return;
    const result = new Date(newDate);
    if (currentDate) {
      result.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0);
    }
    onCommit(result);
  };

  const handleTimeChange = (time: string | null) => {
    if (!time || !currentDate || time === timeString) return;
    const result = new Date(currentDate);
    const [h, m] = time.split(':').map(Number);
    result.setHours(h, m, 0, 0);
    onCommit(result);
  };

  return (
    <div className={cn('flex gap-1', className)}>
      <InputDatePicker
        anchorClassName="flex-1"
        contentClassName="z-100000"
        size="sm"
        mode="single"
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
        selected={currentDate}
        triggerDisabled={disabled}
        onSelect={handleDateSelect}
        startMonth={getDateYearsFromNow(
          -FeConstants.datePickerDefaultYearsOffset,
        )}
        endMonth={getDateYearsFromNow(
          +FeConstants.datePickerDefaultYearsOffset,
        )}
      />
      <TimePicker
        dsSize="sm"
        open={isTimePickerOpen}
        onOpenChange={setIsTimePickerOpen}
        value={timeString}
        onValueChange={handleTimeChange}
        disabled={disabled}
        positionerClassname="z-100000"
      />
    </div>
  );
};

export default DateTimeInput;
